import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

import {
  Thermometer,
  TrendingUp,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   GLOBAL STYLE
═══════════════════════════════════════════════════════════ */

const GLOBAL_STYLE = `
  *{
    box-sizing:border-box;
    margin:0;
    padding:0;
  }

  html,body,#root{
    width:100%;
    height:100%;
    overflow:hidden;
    font-family:Inter,sans-serif;
    background:#0b1120;
    color:white;
  }

  ::-webkit-scrollbar{
    width:4px;
    height:4px;
  }

  ::-webkit-scrollbar-thumb{
    background:rgba(255,255,255,.15);
    border-radius:10px;
  }
`;

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */

const T = {
  bg: "#0b1120",
  surface: "#131c2f",
  border: "rgba(255,255,255,.08)",
  text: "#e2e8f0",
  textMuted: "#64748b",
  orange: "#fb923c",
  green: "#4ade80",
  red: "#f87171",
};

/* ═══════════════════════════════════════════════════════════
   SENSOR META
═══════════════════════════════════════════════════════════ */

const SENSOR_META = {
  temperature: {
    label: "Distillation Room Temperature",
    unit: "°C",
    color: T.orange,
    threshold: 32,
    icon: Thermometer,
  },
};

/* ═══════════════════════════════════════════════════════════
   LIGHTWEIGHT HISTORY GENERATOR
═══════════════════════════════════════════════════════════ */

const genHistory = (
  base,
  variance,
  hours = 24,
  ptPerHour = 1
) => {

  const total = hours * ptPerHour;
  const step = 3600000 / ptPerHour;

  const start = Date.now() - (total * step);

  let value = base;

  return Array.from({ length: total }, (_, i) => {

    value += (Math.random() - 0.5) * variance;

    // smoothing
    value = value * 0.985 + base * 0.015;

    return {
      ts: start + (i * step),
      v: +value.toFixed(2),
    };

  });

};

const HISTORY = {
  temperature: genHistory(28.4, 0.4),
};

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */

const formatTime = ts => {
  return new Date(ts).toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ═══════════════════════════════════════════════════════════
   GLASS CARD
═══════════════════════════════════════════════════════════ */

function GlassCard({ children, style }) {
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        boxShadow: "0 2px 12px rgba(0,0,0,.15)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TOOLTIP
═══════════════════════════════════════════════════════════ */

function CustomTooltip({ active, payload }) {

  if (!active || !payload?.length) return null;

  const item = payload[0];

  return (
    <div
      style={{
        background: "#111827",
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        padding: "8px 12px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: T.textMuted,
          marginBottom: 4,
        }}
      >
        {formatTime(item.payload.ts)}
      </div>

      <div
        style={{
          fontWeight: 700,
          color: T.orange,
          fontSize: 16,
        }}
      >
        {item.value}°C
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */

export default function DetailTemperature() {

  const [live, setLive] = useState({
    temperature: HISTORY.temperature,
  });

  const intervalRef = useRef(null);

  /* ═══════════════════════════════════════════════════════ */

  const pushData = (arr, val) => {

    return [
      ...arr.slice(-23),
      {
        ts: Date.now(),
        v: +val.toFixed(2),
      }
    ];

  };

  /* ═══════════════════════════════════════════════════════ */

  useEffect(() => {

    intervalRef.current = setInterval(() => {

      setLive(prev => {

        const current = prev.temperature.at(-1)?.v ?? 28;

        let next =
          current + ((Math.random() - 0.5) * 0.4);

        // smooth correction
        next = next * 0.985 + 28.4 * 0.015;

        return {
          temperature: pushData(prev.temperature, next),
        };

      });

    }, 60000);

    return () => clearInterval(intervalRef.current);

  }, []);

  /* ═══════════════════════════════════════════════════════ */

  const chartData = useMemo(() => {

    return live.temperature.map(d => ({
      ...d,
      label: formatTime(d.ts),
    }));

  }, [live]);

  /* ═══════════════════════════════════════════════════════ */

  const stats = useMemo(() => {

    const vals = live.temperature.map(d => d.v);

    return {
      current: vals.at(-1),
      avg:
        vals.reduce((a, b) => a + b, 0) / vals.length,
      max: Math.max(...vals),
      min: Math.min(...vals),
    };

  }, [live]);

  /* ═══════════════════════════════════════════════════════ */

  const meta = SENSOR_META.temperature;
  const Icon = meta.icon;

  /* ═══════════════════════════════════════════════════════ */

  return (
    <>
      <style>{GLOBAL_STYLE}</style>

      <div
        style={{
          width: "100%",
          height: "100vh",
          background: T.bg,
          padding: 20,
        }}
      >

        <GlassCard
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >

          {/* HEADER */}

          <div
            style={{
              padding: 20,
              borderBottom: `1px solid ${T.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >

              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: `${meta.color}22`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  size={20}
                  color={meta.color}
                />
              </div>

              <div>

                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  {meta.label}
                </div>

              </div>

            </div>

          </div>

          {/* STATS */}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 12,
              padding: 20,
            }}
          >

            {[
              {
                label: "CURRENT",
                value: stats.current?.toFixed(2),
                color: meta.color,
              },
              {
                label: "AVERAGE",
                value: stats.avg?.toFixed(2),
                color: T.green,
              },
              {
                label: "MAXIMUM",
                value: stats.max?.toFixed(2),
                color: T.red,
              },
              {
                label: "MINIMUM",
                value: stats.min?.toFixed(2),
                color: "#60a5fa",
              },
            ].map(item => (

              <GlassCard
                key={item.label}
                style={{
                  padding: 16,
                }}
              >

                <div
                  style={{
                    fontSize: 11,
                    color: T.textMuted,
                    marginBottom: 8,
                    fontWeight: 600,
                  }}
                >
                  {item.label}
                </div>

                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: item.color,
                  }}
                >
                  {item.value}

                  <span
                    style={{
                      fontSize: 12,
                      marginLeft: 4,
                      color: T.textMuted,
                    }}
                  >
                    °C
                  </span>

                </div>

              </GlassCard>

            ))}

          </div>

          {/* CHART */}

          <div
            style={{
              flex: 1,
              minHeight: 0,
              padding: "0 20px 20px",
            }}
          >

            <GlassCard
              style={{
                width: "100%",
                height: "100%",
                padding: 10,
              }}
            >

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <LineChart
                  data={chartData}
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,.05)"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="label"
                    tick={{
                      fill: T.textMuted,
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />

                  <YAxis
                    tick={{
                      fill: T.textMuted,
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />

                  <Tooltip
                    content={<CustomTooltip />}
                  />

                  <ReferenceLine
                    y={meta.threshold}
                    stroke={T.red}
                    strokeDasharray="4 4"
                  />

                  <Line
                    type="monotone"
                    dataKey="v"
                    stroke={meta.color}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />

                </LineChart>

              </ResponsiveContainer>

            </GlassCard>

          </div>

        </GlassCard>

      </div>
    </>
  );
}